import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Check,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  X as XIcon,
} from "lucide-react";
import toast from "react-hot-toast";
import { setDocData } from "../../Firebase/Firebase.utils";
import { revalidateNews } from "../utils/pressCoverage";
import EmptyState from "./EmptyState";

// Review Queue for /cms/press-coverage. Three sub-tabs by status:
//
//   pending  — freshly ingested by the daily cron. Not on /news yet.
//   approved — shown on public /news.
//   rejected — hidden from /news AND kept as tombstones so the daily
//              cron doesn't re-add the same URL on its next run.
//
// Bulk actions: per-row checkboxes + a sticky action bar. Approving a
// batch flips their status and pushes a single /news revalidate.
//
// Never hard-deletes. "Rejected" IS the tombstone state — the doc id
// (SHA-1 of the source URL) is what the cron's dedup checks against.
export default function PressCoverageReviewQueue({
  docs,
  loading,
  onChanged,
}) {
  const currentUser = useSelector((state) => state.userObject);
  const [tab, setTab] = useState("pending");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  // Effective status — see the same fallback used by the public
  // getMergedNewsFeed: explicit `status` wins; legacy docs written
  // pre-review-queue used `visible` and are treated as approved (true
  // / missing) or rejected (false).
  const effectiveStatus = useCallback((doc) => {
    if (typeof doc.status === "string") return doc.status;
    if (doc.visible === false) return "rejected";
    return "approved";
  }, []);

  const bucketed = useMemo(() => {
    const buckets = { pending: [], approved: [], rejected: [] };
    for (const d of docs || []) {
      const s = effectiveStatus(d);
      if (buckets[s]) buckets[s].push(d);
    }
    // Newest first within each bucket.
    for (const key of Object.keys(buckets)) {
      buckets[key].sort((a, b) => {
        const at = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
        const bt = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
        return bt - at;
      });
    }
    return buckets;
  }, [docs, effectiveStatus]);

  const visible = bucketed[tab] || [];
  const selectedInTab = useMemo(() => {
    const s = new Set();
    for (const doc of visible) if (selected.has(doc.id)) s.add(doc.id);
    return s;
  }, [visible, selected]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = visible.every((d) => next.has(d.id));
      if (allSelected) for (const d of visible) next.delete(d.id);
      else for (const d of visible) next.add(d.id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const bulkSetStatus = async (targetStatus) => {
    const ids = Array.from(selectedInTab);
    if (ids.length === 0) return;
    setBusy(true);
    const nowIso = new Date().toISOString();
    const email = currentUser?.email || null;
    let ok = 0;
    let fail = 0;
    // Serial writes keep this simple + easy to reason about; the batch
    // is small (dozens at most) so parallelism isn't worth the code.
    for (const id of ids) {
      const doc = (docs || []).find((d) => d.id === id);
      if (!doc) {
        fail += 1;
        continue;
      }
      try {
        await setDocData("newsRss", id, {
          ...doc,
          status: targetStatus,
          // Drop the legacy `visible` field on the way through — the
          // record is now status-driven. Explicit false so any client
          // still reading `visible` sees rejected/approved consistently
          // via effectiveStatus.
          visible: targetStatus === "approved",
          reviewedBy: email,
          reviewedAt: nowIso,
          updatedAt: nowIso,
          updatedBy: email,
        });
        ok += 1;
      } catch (err) {
        console.error("[press-coverage] bulk write failed for", id, err);
        fail += 1;
      }
    }
    if (ok > 0) {
      toast.success(`${ok} ${labelForStatus(targetStatus)}.`);
      // One revalidate for the whole batch — the public /news feed
      // only cares that the collection changed, not per-doc.
      revalidateNews();
    }
    if (fail > 0) toast.error(`${fail} write(s) failed.`);
    clearSelection();
    onChanged?.();
    setBusy(false);
  };

  if (loading) {
    return <div className="text-sm text-gray-500">Loading review queue…</div>;
  }

  const allSelectedInTab =
    visible.length > 0 && visible.every((d) => selected.has(d.id));

  return (
    <div>
      {/* Status tabs */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {[
          { key: "pending", label: "Pending", tone: "amber" },
          { key: "approved", label: "Approved", tone: "emerald" },
          { key: "rejected", label: "Rejected", tone: "gray" },
        ].map((t) => (
          <TabChip
            key={t.key}
            label={t.label}
            count={bucketed[t.key].length}
            active={tab === t.key}
            onClick={() => {
              setTab(t.key);
              clearSelection();
            }}
            tone={t.tone}
          />
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          Icon={tab === "pending" ? ImageIcon : Check}
          title={
            tab === "pending"
              ? "No press coverage awaiting review"
              : tab === "approved"
                ? "No approved coverage yet"
                : "No rejected items"
          }
          message={
            tab === "pending"
              ? "The daily cron runs each morning and drops new Leechiu mentions here for you to approve or reject."
              : tab === "approved"
                ? "Approve items from the Pending tab to publish them on /news."
                : "Rejections stay here as tombstones so the daily cron won't re-add the same articles. Re-approve any if needed."
          }
        />
      ) : (
        <>
          {/* Bulk action bar */}
          <div className="sticky top-0 z-10 mb-3 bg-white rounded-lg ring-1 ring-gray-100 shadow-sm flex flex-wrap items-center gap-3 px-4 py-2.5">
            <label className="inline-flex items-center gap-2 text-xs text-gray-700 select-none">
              <input
                type="checkbox"
                checked={allSelectedInTab}
                onChange={toggleAll}
                className="rounded border-gray-300 text-leechiuBlue focus:ring-leechiuBlue"
              />
              {selectedInTab.size > 0
                ? `${selectedInTab.size} selected`
                : `Select all ${visible.length}`}
            </label>
            {selectedInTab.size > 0 ? (
              <>
                <div className="h-4 w-px bg-gray-200" />
                {tab !== "approved" ? (
                  <button
                    type="button"
                    onClick={() => bulkSetStatus("approved")}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {busy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                    Approve selected
                  </button>
                ) : null}
                {tab !== "rejected" ? (
                  <button
                    type="button"
                    onClick={() => bulkSetStatus("rejected")}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white text-red-700 ring-1 ring-red-200 hover:bg-red-50 disabled:opacity-60"
                  >
                    {busy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <XIcon className="w-3.5 h-3.5" />
                    )}
                    Reject selected
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={clearSelection}
                  disabled={busy}
                  className="ml-auto text-xs text-gray-500 hover:text-gray-700"
                >
                  Clear selection
                </button>
              </>
            ) : null}
          </div>

          {/* Row list */}
          <ul className="space-y-2">
            {visible.map((doc) => (
              <Row
                key={doc.id}
                doc={doc}
                status={effectiveStatus(doc)}
                selected={selected.has(doc.id)}
                onToggleSelect={() => toggleOne(doc.id)}
                onApprove={
                  effectiveStatus(doc) === "approved"
                    ? null
                    : () => bulkSetStatusForOne(doc, "approved")
                }
                onReject={
                  effectiveStatus(doc) === "rejected"
                    ? null
                    : () => bulkSetStatusForOne(doc, "rejected")
                }
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );

  // Inline single-row action — reuses the bulk path with a one-item
  // set so behavior stays identical (same fields written, same
  // revalidate, same optimistic UI).
  async function bulkSetStatusForOne(doc, targetStatus) {
    setSelected(new Set([doc.id]));
    // Give React a tick to update selection before firing the bulk
    // handler; guarantees the "selectedInTab" set includes it.
    await new Promise((r) => setTimeout(r, 0));
    await bulkSetStatus(targetStatus);
  }
}

function labelForStatus(s) {
  if (s === "approved") return "approved and pushed to /news";
  if (s === "rejected") return "rejected";
  return s;
}

function TabChip({ label, count, active, onClick, tone }) {
  const toneClasses = {
    amber: active
      ? "bg-amber-500 text-white"
      : "bg-white text-amber-800 ring-1 ring-amber-200 hover:bg-amber-50",
    emerald: active
      ? "bg-emerald-600 text-white"
      : "bg-white text-emerald-800 ring-1 ring-emerald-200 hover:bg-emerald-50",
    gray: active
      ? "bg-gray-800 text-white"
      : "bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${toneClasses[tone] || toneClasses.gray}`}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );
}

function Row({
  doc,
  status,
  selected,
  onToggleSelect,
  onApprove,
  onReject,
}) {
  const dateLabel = doc.publishedAt
    ? new Date(doc.publishedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";
  const badge =
    status === "approved"
      ? { text: "Approved", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" }
      : status === "rejected"
        ? { text: "Rejected", cls: "bg-gray-100 text-gray-600 ring-gray-200" }
        : { text: "Pending", cls: "bg-amber-50 text-amber-800 ring-amber-200" };

  return (
    <li className="bg-white rounded-xl ring-1 ring-gray-100 shadow-sm flex items-center gap-3 pl-3 pr-2 py-3">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggleSelect}
        aria-label="Select for bulk action"
        className="rounded border-gray-300 text-leechiuBlue focus:ring-leechiuBlue shrink-0"
      />
      <div className="shrink-0 h-14 w-14 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
        {doc.coverPhoto ? (
          <img
            src={doc.coverPhoto}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <ImageIcon className="w-5 h-5 text-gray-300" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={doc.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold text-leechiuBlue hover:text-leechiuOrange truncate"
          >
            {doc.title || "Untitled"}
          </a>
          <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${badge.cls}`}
          >
            {badge.text}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-gray-500 uppercase tracking-wide mt-1">
          {dateLabel ? <span>{dateLabel}</span> : null}
          {dateLabel && doc.publisher ? <span aria-hidden="true">·</span> : null}
          {doc.publisher ? <span className="truncate">{doc.publisher}</span> : null}
        </div>
        {doc.description ? (
          <p className="text-xs text-gray-500 line-clamp-1 mt-1">
            {doc.description}
          </p>
        ) : null}
      </div>
      <div className="shrink-0 flex items-center gap-1">
        <a
          href={doc.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-gray-600 hover:bg-gray-50"
          title="Open source"
        >
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        {onApprove ? (
          <button
            type="button"
            onClick={onApprove}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-emerald-700 hover:bg-emerald-50"
          >
            <Check className="w-3.5 h-3.5" />
            Approve
          </button>
        ) : null}
        {onReject ? (
          <button
            type="button"
            onClick={onReject}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-red-700 hover:bg-red-50"
          >
            <XIcon className="w-3.5 h-3.5" />
            Reject
          </button>
        ) : null}
      </div>
    </li>
  );
}
